import * as ReactDOMServer from 'react-dom/server';
import { createContext, type FC, isValidElement, type ReactNode, useContext, useEffect, useRef, useState } from "react"
import * as Leaflet from 'leaflet';
import * as ReactLeaflet from 'react-leaflet';
import MarkerClusterGroup from "react-leaflet-cluster";

import AIOInput from "../aio-input"
import 'leaflet/dist/leaflet.css';
import { Debounce } from '../aio-utils';
import simplify from 'simplify-js'
import './index.css';

export type I_pos = [number, number]
export type I_marker = { pos: [number, number], html?: ReactNode, eventHandlers?: any }
export type I_shapeStyle = {
    stroke?: { color?: string, width?: number, dash?: string },
    fill?: { color?: string, opacity?: number }
}
export type I_circle = { type: 'circle', center: I_pos, radius?: number, style?: I_shapeStyle }
export type I_rect = { type: 'rect', points: I_pos[], style?: I_shapeStyle }
export type I_polyline = { type: 'polyline', points: I_pos[], style?: I_shapeStyle }
export type I_shape = I_circle | I_rect | I_polyline
export type I_Map = {
    children?: React.ReactNode,
    onChangePos?: (coords: I_pos) => void,
    onChangeZoom?: (newZoom: number) => void,
    pos?: I_pos,
    delay?: number,
    zoom?: number,
    zoomOptions?: { wheel?: boolean, control?: boolean },
    markers?: I_marker[]
    marker?: ReactNode | false,
    mapStyle?: any,
    attrs?: any,
    onClick?: () => void,
    dragging?: boolean,
    submitText?: string,
    onSubmit?: (pos: I_pos) => void,
    shapes?: I_shape[],
    footer?: ReactNode,
    layers?: I_layers,
    getSearchResult?: (searchValue: string) => Promise<I_searchResult[]>,
    onSearch?: (searchResult: I_searchResult) => void,
    mapRef?: any,
    whenReady?: () => void,
    onMoveEnd?: (e: Leaflet.LeafletEvent) => void,
    actionsRef?: any,
    tileAttrs?: any
}
export type I_layers = { position: 'topright' | 'topleft', items: I_layerItem[] }
export type I_layerItem = { name: string, markers?: I_marker[], shapes?: I_shape[], active?: boolean, cluster?: boolean }
type I_mapctx = {
    rootProps: I_Map, pos: I_pos,
    changePos: (pos: I_pos) => void, setMap: any,
    changeZoom: (zoom: number, caller: string) => void, zoom: number,
    getDefaultMarkerIcon: () => ReactNode,
}
const MAPCTX = createContext({} as any)
const AIMap: FC<I_Map> = (props) => {
    const { pos: propsPos = [35.699939, 51.338497], getSearchResult, mapRef, zoom: propsZoom = 14, onChangePos = () => { }, delay = 500 } = props;
    const [map, setMap] = useState<any>(null)
    const [z, setZoom] = useState<number>(propsZoom)
    const zoomRef = useRef<number>(z)
    zoomRef.current = z;
    const [p, setPos] = useState<I_pos>(propsPos)
    const posRef = useRef<I_pos>(p)
    posRef.current = p;
    function changeZoom(newZoom: number) {
        if (newZoom === zoomRef.current) { return }
        setZoom(newZoom)
    }
    if (mapRef) { mapRef.current = map; }
    if (props.actionsRef) {
        props.actionsRef.current = {
            flyTo: (p: { lat: number, lng: number, zoom?: number, callback: () => void }) => {
                const zoom = p.zoom || map.getZoom()
                map.flyTo([p.lat, p.lng], zoom)
                const onMoveEnd = () => {
                    map.off("moveend", onMoveEnd);
                    if (zoom !== zoomRef.current) {
                        changeZoom(zoom)
                        p.callback();
                    }
                }
                map.on("moveend", onMoveEnd);
            }
        }
    }
    const changePos = (newPos: I_pos) => {
        if (newPos[0] === posRef.current[0] && newPos[1] === posRef.current[1]) { return }
        setPos(newPos);
        Debounce(() => onChangePos(newPos), delay)
    }
    function getDefaultMarkerIcon() {
        return <div className='marker-icon'><div className='marker-icon-circle'></div><div className='marker-icon-arrow'></div></div>
    }
    function getContext(): I_mapctx { return { pos: posRef.current, setMap, rootProps: props, changePos, getDefaultMarkerIcon, changeZoom, zoom: zoomRef.current } }
    useEffect(() => {
        if (map !== null) { map.setView(propsPos, zoomRef.current, { animate: false }) }
        setPos(propsPos)
    }, [propsPos[0] + '-' + propsPos[1]])
    useEffect(() => {
        setZoom(propsZoom)
    }, [propsZoom])
    const attrs = AddToAttrs(props.attrs, { className: 'ai-map' })
    return (
        <MAPCTX.Provider value={getContext()}>
            <div {...attrs}>
                {!!getSearchResult && <MapHeader />}
                <MapBody />
                <MapFooter />
            </div>
        </MAPCTX.Provider>
    );
};
export default AIMap
const MapBody: FC = () => {
    const { rootProps, pos, setMap, getDefaultMarkerIcon, zoom }: I_mapctx = useContext(MAPCTX)
    const { mapStyle, dragging = true, children, shapes = [], marker, markers = [], whenReady } = rootProps
    const defaultStyle = { width: '100%', height: '100%' }
    return (
        <ReactLeaflet.MapContainer
            center={pos} style={{ ...defaultStyle, ...mapStyle }} zoom={zoom} scrollWheelZoom={rootProps.zoomOptions?.wheel ? 'center' : undefined} zoomControl={rootProps.zoomOptions?.control !== false}
            attributionControl={true} dragging={dragging} ref={setMap} whenReady={whenReady}
        >
            {!rootProps.tileAttrs && <ReactLeaflet.TileLayer url="https://{s}.tile.openstreetmap.de/{z}/{x}/{y}.png" />}
            {!!rootProps.tileAttrs && <ReactLeaflet.TileLayer {...rootProps.tileAttrs}/>}
            {/* <TileLayer 
              url="https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token={accessToken}" 
              attribution='<a href="https://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'     
              accessToken='pk.eyJ1IjoibXNmMTM2NCIsImEiOiJjbTE1MnlpM20wNTJvMmtyNDhjYjIzMXRhIn0.zRM2a68bNyBsYSeIdV8a4A'
            /> */}
            {/* http://leaflet-extras.github.io/leaflet-providers/preview/ */}
            <MapEvents />
            {marker !== false && <MapMarker key='main-marker' pos={pos} html={isValidElement(marker) ? marker : getDefaultMarkerIcon()} />}
            <MarkerClusterGroup chunkedLoading showCoverageOnHover={false}>
                {markers.map((marker: I_marker, i: number) => <MapMarker key={`marker-${i}`} pos={marker.pos} html={marker.html} eventHandlers={marker.eventHandlers} />)}
            </MarkerClusterGroup>
            {shapes.map((o: I_shape, i: number) => <MapShape key={i} shape={o} />)}
            <MapLayers />
            {children}
        </ReactLeaflet.MapContainer>
    )
}
type I_searchResult = { text: string, value: string, pos: I_pos, subtext?: string, before?: ReactNode, after?: ReactNode }
const MagnifyIcon: FC = () => {
    return (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width={24} height={24}>
            <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
        </svg>
    )
}
const MapHeader: FC = () => {
    const { rootProps }: I_mapctx = useContext(MAPCTX);
    const { getSearchResult, onSearch } = rootProps;
    const [searchValue, setSearchValue] = useState<string>('')
    const [searchResult, setSerachResult] = useState<I_searchResult[]>([])
    const timeout = useRef<any>(undefined);
    function changeSearch(searchValue: string) {
        setSearchValue(searchValue);
        clearTimeout(timeout.current);
        timeout.current = setTimeout(async () => {
            if (getSearchResult) {
                const res: I_searchResult[] = await getSearchResult(searchValue)
                if (Array.isArray(res)) { setSerachResult(res) }
            }
        }, 1200)
    }
    return (
        <div className="ai-map-header">
            <AIOInput
                type='text' value={searchValue} options={(searchResult || [])}
                before={<MagnifyIcon />}
                onChange={(searchValue) => changeSearch(searchValue)}
                option={{
                    onClick: (option) => { if (onSearch) { onSearch(option as I_searchResult) } }
                }}
            />
        </div>
    )
}
const MapLayers: FC = () => {
    const { rootProps }: I_mapctx = useContext(MAPCTX);
    const { layers } = rootProps;
    if (!layers) { return null }
    const { position = 'topright', items = [] } = layers;
    return (
        <ReactLeaflet.LayersControl position={position}>
            {
                items.map((o: I_layerItem, i: number) => {
                    const { shapes = [], markers = [], active = true, cluster } = o;
                    const content = markers.map((marker: I_marker, j: number) =>
                        <MapMarker key={j} pos={marker.pos} html={marker.html} eventHandlers={marker.eventHandlers} />
                    )
                    return (
                        <ReactLeaflet.LayersControl.Overlay name={o.name} checked={active} key={i}>
                            <ReactLeaflet.FeatureGroup>
                                {!!cluster && <MarkerClusterGroup chunkedLoading showCoverageOnHover={false}>{content}</MarkerClusterGroup>}
                                {!cluster && content}
                                {shapes.map((shape: I_shape, k: number) =>
                                    <MapShape key={k} shape={shape} />
                                )}
                            </ReactLeaflet.FeatureGroup>
                        </ReactLeaflet.LayersControl.Overlay>
                    )
                })
            }
        </ReactLeaflet.LayersControl>
    )
}
const MapShape: FC<{ shape: I_shape }> = ({ shape }) => {
    const { style = {}, type } = shape, { stroke = {}, fill = {} } = style;
    const { width = 4, dash, color: strokeColor = 'orange' } = stroke;
    const { color: fillColor = 'orange', opacity = 0.3 } = fill;
    const pathOptions = { fillColor: fillColor, color: strokeColor, fillOpacity: opacity, weight: width, dashArray: dash }
    if (type === 'circle') {
        const { center, radius = 100 } = shape;
        return (<ReactLeaflet.Circle center={center} pathOptions={pathOptions} radius={radius} />)
    }
    else if (type === 'rect') { return (<ReactLeaflet.Rectangle bounds={shape.points} pathOptions={pathOptions} />) }
    else if (type === 'polyline') { return (<ReactLeaflet.Polyline positions={shape.points} pathOptions={pathOptions} />) }
    return null
}
const MapFooter: FC = () => {
    const { rootProps, pos }: I_mapctx = useContext(MAPCTX);
    const { submitText = 'Submit', onSubmit, footer } = rootProps;
    if (!onSubmit && !footer) { return null }
    return (
        <div className="ai-map-footer">
            {!!onSubmit && <button type='button' onClick={() => onSubmit(pos)}>{submitText}</button>}
            <div className="ai-map-footer-html">{footer || null}</div>
        </div>
    )
}
const MapMarker: FC<{ pos: I_pos, html?: ReactNode, eventHandlers?: any }> = ({ pos, html, eventHandlers }) => {
    const { getDefaultMarkerIcon }: I_mapctx = useContext(MAPCTX)
    function getHtmlIcon(html: ReactNode) {
        return Leaflet.divIcon({
            html: JSXToHTML(html),
            className: '', // Optional, for adding custom styles
            iconSize: [32, 32], // size of the icon
            iconAnchor: [16, 32], // point of the icon which will correspond to marker's location
        });
    }
    html = html || getDefaultMarkerIcon()
    let props: any = { position: pos }
    if (html) { props.icon = getHtmlIcon(html) }
    return <ReactLeaflet.Marker {...props} animate={false} eventHandlers={eventHandlers} />
}
function MapEvents() {
    const { rootProps, changePos, changeZoom }: I_mapctx = useContext(MAPCTX)
    ReactLeaflet.useMapEvents({
        click: () => rootProps.onClick ? rootProps.onClick() : undefined,
        move: (e: any) => {
            if (rootProps.dragging === false) { return }
            let { lat, lng } = e.target.getCenter()
            changePos([lat, lng])
        },
        zoom: (e: any) => {
            changeZoom(e.target._zoom, 'useMapEvents')
        },
        locationfound: (location: any) => {
            console.log('location found:', location)
        },
        moveend: (e) => {
            if (rootProps.onMoveEnd) { rootProps.onMoveEnd(e) }
        },
        movestart: () => {
        }
    })
    return null
}
function JSXToHTML(jsx: any): string {
    return ReactDOMServer.renderToStaticMarkup(jsx)
}
function classListToString(classes: any[]) {
    let list: string[] = [];
    const getClasses = (cls: any): string[] => {
        if (Array.isArray(cls)) {
            return cls.filter((o) => !!o && typeof o === 'string')
        }
        if (typeof cls === 'string') { return cls.split(' ') }
        return []
    }
    for (let i = 0; i < classes.length; i++) {
        const cls = classes[i];
        list = [...list, ...getClasses(cls)]
    }
    return list.length ? list.join(' ') : ''
}
function AddToAttrs(attrs: any, p: any) {
    attrs = attrs || {};
    let { style } = p;
    const className = classListToString([attrs.className, p.className])
    let newStyle = { ...attrs.style, ...style };
    return { ...attrs, className, style: newStyle, ...p.attrs }
}
export const smooth = <T,>(p:{data: T[], acc?: 'low' | 'medium' | 'high',getLat:(row:T)=>number,getLng:(row:T)=>number}):T[] => {
    const acc = p.acc || 'medium'
    const points2D:{x:number,y:number,row:T}[] = p.data.map((o: T) => ({ x: p.getLng(o), y: p.getLat(o),row:o })) // simplify-js با x,y کار می‌کنه
    const simplified = simplify(points2D, acc === 'low' ? 0.001 : (acc === 'medium' ? 0.002 : 0.003), true) as {x:number,y:number,row:T}[] // 10 = tolerance (متر تقریبی), true = highQuality
    return simplified.map(p => p.row)
}
export function getCenterOfPoints(points: { lat: number, lng: number }[]): { lat: number, lng: number } {
    const filteredPoints = points.filter(({ lat, lng }) => typeof lat === 'number' && typeof lng === 'number')
    let totalLat = 0, totalLng = 0
    for (let i = 0; i < filteredPoints.length; i++) {
        const { lat, lng } = filteredPoints[i];
        totalLat += lat;
        totalLng += lng
    }
    totalLat /= filteredPoints.length
    totalLng /= filteredPoints.length
    return { lat: totalLat, lng: totalLng }
}